// =============================================================================
// features/meu-whatsapp/MeuWhatsAppInboxPage.tsx — Caixa pessoal do vendedor
// (F43-S13, doc planejamento-inbox-vendedores-waha.md §3.8).
//
// NÃO é uma tela nova de conversa — é um RECORTE da caixa geral (`/conversas`),
// filtrado pelo canal WAHA do vendedor logado (`ConversationsLayout` +
// `ChatList` ganharam `fixedChannelId` para isso). O backend já suporta o
// filtro por `channelId` desde a F16 — nada novo na API.
//
// Composição desta tela:
//   - Selo de canal por conversa: já existe em `ChatListItem` (ícone de
//     provider no avatar) — nenhuma mudança necessária aqui.
//   - Estado da sessão + aviso ANTES de digitar: `SessionStatusBanner`,
//     alimentado pelo polling já existente de `useMeuPareamento` (F39-S08).
//   - Trava de IA, visível SÓ para quem tem `channel.ai_autoreply:manage`
//     (somente `admin`, F43-S09/S15): `AiAutoreplyToggle`. Esconder aqui é
//     cosmético — o backend é quem nega de verdade.
//
// Gate (mesmo padrão de `useStartConversation.ts`): a flag de módulo
// `waha.inbox.enabled` ainda não foi seedada no backend (fora de
// `files_allowed` deste slot — seeds/migrations são `apps/api/src/db/**`).
// `useFeatureFlag` cai no fallback fail-closed documentado em
// `hooks/useFeatureFlag.ts`, então esta rota fica invisível até um slot de
// backend seedar a flag — igual a PWA/F27, Respostas Rápidas/F28 e
// "Iniciar conversa"/F43-S11. Intencional: o inbound/outbound WAHA (Blocos
// 1 e 2 do plano) ainda não estão todos mergeados nesta sessão de trabalho.
//
// LGPD (doc 17 §8.1): mesma superfície de `/conversas` — nenhum dado novo.
// =============================================================================

import { WHATSAPP_MONITORING_POLICY_KEY } from '@elemento/shared-schemas';
import * as React from 'react';
import { Link, Navigate } from 'react-router-dom';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-store';
import { ConversationsLayout } from '../conversations/components/ConversationsLayout';
import { MonitoringBanner } from '../policies/MonitoringBanner';
import { PolicyAcceptanceGate } from '../policies/PolicyAcceptanceGate';

import { AiAutoreplyToggle } from './AiAutoreplyToggle';
import { SessionStatusBanner } from './SessionStatusBanner';
import { useMeuPareamento } from './useMeuWhatsApp';
import { useSessionStatusRealtime } from './useSessionStatusRealtime';

const AI_AUTOREPLY_PERMISSION = 'channel.ai_autoreply:manage';

// ---------------------------------------------------------------------------
// Pill de status — resumo compacto ao lado do título (complementa o banner,
// que só aparece quando a sessão está fora do ar).
// ---------------------------------------------------------------------------

function StatusPill({ working }: { readonly working: boolean }): React.JSX.Element {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 font-sans text-xs font-semibold"
      style={{
        background: working ? 'var(--success-bg)' : 'var(--surface-muted)',
        color: working ? 'var(--success)' : 'var(--text-3)',
      }}
    >
      <span
        aria-hidden="true"
        className="rounded-full"
        style={{
          width: 6,
          height: 6,
          background: working ? 'var(--success)' : 'var(--text-3)',
        }}
      />
      {working ? 'Conectado' : 'Não conectado'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Estado vazio — vendedor sem número pareado ainda
// ---------------------------------------------------------------------------

function NotConnectedState(): React.JSX.Element {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
      <p
        className="font-sans font-medium"
        style={{ fontSize: 'var(--text-base)', color: 'var(--text)' }}
      >
        Você ainda não conectou seu WhatsApp
      </p>
      <p
        style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)' }}
        className="font-sans max-w-sm"
      >
        Conecte seu celular para que suas conversas apareçam aqui, separadas da caixa oficial.
      </p>
      <Link
        to="/meu-whatsapp"
        className="font-sans text-sm font-semibold px-4 py-2.5 rounded-sm bg-azul text-white transition-[filter] duration-fast hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-azul/30"
      >
        Conectar meu WhatsApp
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MeuWhatsAppInboxPage
// ---------------------------------------------------------------------------

export function MeuWhatsAppInboxPage(): React.JSX.Element {
  const { enabled: flagEnabled, isLoading: flagLoading } = useFeatureFlag('waha.inbox.enabled');
  const { hasPermission } = useAuth();
  const canManageAi = hasPermission(AI_AUTOREPLY_PERMISSION);

  // F43-S12: aqui é onde o vendedor realmente está (não só em /meu-whatsapp) —
  // é onde o aviso de sessão caída precisa reagir sem reload.
  useSessionStatusRealtime();

  const me = useMeuPareamento();

  if (flagLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]" role="status">
        <div className="w-6 h-6 rounded-full border-2 border-border-strong border-t-azul animate-spin" />
      </div>
    );
  }

  if (!flagEnabled) return <Navigate to="/404" replace />;

  if (me.isError && me.error instanceof ApiError && me.error.status === 404) {
    return (
      <div className="max-w-lg mx-auto py-12 text-center">
        <h1 className="font-display font-bold text-ink" style={{ fontSize: 'var(--text-2xl)' }}>
          Seu acesso ainda não está pronto
        </h1>
        <p className="font-sans text-sm text-ink-3 mt-2">{me.error.message}</p>
      </div>
    );
  }

  const channelId = me.data?.channel_id ?? null;
  const status = me.data?.status ?? 'STOPPED';
  const connected = status === 'WORKING';

  // F43-S12: composer some com a explicação em vez de deixar o vendedor
  // escrever algo que nunca sairia — o `SessionStatusBanner` já diz o QUE
  // aconteceu (estado detalhado por status); aqui é só o motivo por que o
  // campo de texto sumiu.
  const composerDisabledReason =
    channelId !== null && !connected
      ? 'Seu WhatsApp está desconectado — reconecte para enviar mensagens por este número.'
      : undefined;

  return (
    <PolicyAcceptanceGate policyKey={WHATSAPP_MONITORING_POLICY_KEY}>
      <div
        className="-m-6 flex flex-col overflow-hidden"
        style={{ height: 'calc(100vh - 3.5rem)' }}
      >
        {/* Cabeçalho: título + status + trava de IA (só admin) */}
        <div
          className="flex flex-wrap items-center justify-between gap-4 px-6 py-4"
          style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-elev-1)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <h1
              className="font-display font-bold text-ink leading-tight truncate"
              style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
            >
              Minhas conversas
            </h1>
            {channelId !== null && <StatusPill working={connected} />}
          </div>

          {channelId !== null && canManageAi && <AiAutoreplyToggle channelId={channelId} />}
        </div>

        {/* Aviso permanente: linha corporativa monitorada (F51-S17) — sempre
            visível, independente do estado da sessão. */}
        <div className="px-6 pt-3">
          <MonitoringBanner />
        </div>

        {/* Aviso ANTES de digitar — silencioso quando a sessão está saudável */}
        {channelId !== null && <SessionStatusBanner status={status} />}

        {channelId === null ? (
          <NotConnectedState />
        ) : (
          <ConversationsLayout
            fixedChannelId={channelId}
            {...(composerDisabledReason !== undefined ? { composerDisabledReason } : {})}
          />
        )}
      </div>
    </PolicyAcceptanceGate>
  );
}
